import { useFormContext } from 'react-hook-form';
import { WizardSummary } from '@/components/wizards';
import {
  HEALTH_BUSINESS_TYPE_OPTIONS,
  HEALTH_MEMBER_RELATIONSHIP_OPTIONS,
  HEALTH_SUM_INSURED_OPTIONS,
  calculateAge,
  type HealthQuoteFormData,
} from '../../schema';
import { formatCurrency } from '@/utils';

interface ReviewStepProps {
  proposerName: string;
  proposerPhone: string;
  proposerEmail?: string;
}

const YES_NO_LABEL: Record<string, string> = { yes: 'Yes', no: 'No' };

function labelFor(options: { value: string; label: string }[], value?: string): string {
  return options.find((o) => o.value === value)?.label || '-';
}

function currency(value?: string): string {
  if (!value) return '-';
  const n = Number(value);
  return Number.isFinite(n) ? formatCurrency(n) : '-';
}

export function ReviewStep({ proposerName, proposerPhone, proposerEmail }: ReviewStepProps) {
  const { watch } = useFormContext<HealthQuoteFormData>();
  const data = watch();

  const memberItems = [
    ...(data.adult_members ?? []).map((m, i) => ({
      label: `Adult ${i + 1}`,
      value: `${labelFor(HEALTH_MEMBER_RELATIONSHIP_OPTIONS, m.relation)} — ${m.date_of_birth || '-'}${
        m.date_of_birth ? ` (${calculateAge(m.date_of_birth) ?? '-'} yrs)` : ''
      }`,
    })),
    ...(data.child_members ?? []).map((m, i) => ({
      label: `Child ${i + 1}`,
      value: `${labelFor(HEALTH_MEMBER_RELATIONSHIP_OPTIONS, m.relation)} — ${m.date_of_birth || '-'}${
        m.date_of_birth ? ` (${calculateAge(m.date_of_birth) ?? '-'} yrs)` : ''
      }`,
    })),
  ];

  const sections = [
    {
      title: 'Proposer',
      items: [
        { label: 'Name', value: proposerName || '-' },
        { label: 'Phone', value: proposerPhone || '-' },
        { label: 'Email', value: proposerEmail || '-' },
      ],
    },
    {
      title: 'Product Details',
      items: [
        { label: 'Business Type', value: labelFor([...HEALTH_BUSINESS_TYPE_OPTIONS], data.business_type) },
        { label: 'PIN Code', value: data.pincode || '-' },
        { label: 'State', value: data.state || '-' },
        { label: 'City', value: data.city || '-' },
      ],
    },
    {
      title: 'Insured Members',
      items: [
        { label: 'Adults', value: data.adults || '-' },
        { label: 'Children', value: data.children || '-' },
        ...memberItems,
      ],
    },
    {
      title: 'Premium Details',
      items: [
        { label: 'Policy Tenure', value: data.policy_term_years ? `${data.policy_term_years} years` : '-' },
        { label: 'Sum Insured', value: labelFor(HEALTH_SUM_INSURED_OPTIONS, data.sum_insured) },
      ],
    },
    {
      title: 'Additional Details',
      items: [
        { label: 'Applicant Annual Income', value: currency(data.applicant_annual_income) },
        { label: 'Pre-existing Medical Condition', value: YES_NO_LABEL[data.pre_existing_condition] || '-' },
        { label: 'NRI Discount', value: YES_NO_LABEL[data.nri_discount] || '-' },
        { label: 'Bureau Score Discount', value: YES_NO_LABEL[data.bureau_score_discount] || '-' },
      ],
    },
  ];

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review & Submit</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Confirm the details below before calculating the premium.
      </p>
      <WizardSummary sections={sections} />
    </div>
  );
}
